import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CloudUpload } from '@material-ui/icons';
import { observer } from 'mobx-react';
import { useDropzone } from 'react-dropzone';
import { NewFileUpload, NewFileUploadItem } from './newFileUploadModal';
import { useEffect, useState } from 'react';
import { Document } from '@roc/feature-types';
import { areActionsUnavailableBasedOnDocument } from '@roc/feature-general-tasks';

const useStyles = makeStyles(() => ({
  uploadBlocked: {
    border: '1px solid #CBCBCB',
    width: '100%',
    minHeight: '200px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: 20,
    marginBottom: 10,
    borderRadius: 5,
  },
  dragArea: {
    border: '1px solid #CBCBCB',
    width: '100%',
    minHeight: '200px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: 20,
    marginBottom: 10,
    borderRadius: 5,
    cursor: 'pointer',
  },
  errorDragArea: {
    border: '2px dotted #D10A0A',
    width: '100%',
    minHeight: '120px',
    backgroundColor: '#EAF4FA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: 20,
    marginBottom: 10,
    borderRadius: 5,
    cursor: 'pointer',
  },
  icon: {
    minWidth: '20px',
    minHeight: '20px',
    width: '3vw',
    height: '3vw',
  },
  fileName: {
    marginLeft: 5,
    marginRight: 2,
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  errorMeasage: {
    color: '#D10A0A',
  },
  uploadText: {
    fontWeight: 500,
  },
  uploadTextV2: {
    marginLeft: 16,
    fontWeight: 600,
  }
}));

interface IFileUploadAreaProps {
  items: NewFileUpload[];
  setItems: (items) => void;
  itemDocument?: Document;
  multiple?: boolean;
  fileType?: string;
  fileTypes?: string[];
  showError?: boolean;
  uploadVersion?: number;
  maxFiles?: number;
  showSimulateDeleteButton?: boolean;
  showUploadButton?: boolean;
}

export const NewFileUploadArea = observer((
  {
    items, setItems, itemDocument, multiple, fileType, showError, fileTypes, maxFiles,
    showUploadButton = false, uploadVersion, showSimulateDeleteButton = false
  }: IFileUploadAreaProps
) => {
    const classes = useStyles();
    const [error, setError] = useState<boolean>(false);
    const UPLOAD_VERSION_2 = 2;
    const isCorrectFileType = (files: any[]) => {
      let isValid = true;
      if (fileType) {
        files.forEach(file => {
          if (file.type?.toLowerCase() !== fileType.toLowerCase()) {
            isValid = false;
            return;
          }
        })
      }

      if (fileTypes) {
        files.forEach(file => {
          const dotSplitFileName = file.name.split('.');
          if (!fileTypes.includes(dotSplitFileName[dotSplitFileName.length - 1])) {
            isValid = false;
            return;
          }
        });
      }

      return isValid;
    }

    const onDrop = (acceptedFiles: File[]) => {
      const newItems = acceptedFiles.map(file => ({
        name: file.name,
        file: file,
        type: file.name.split('.')[1]
      }));
      if (isCorrectFileType(newItems)) {
        setItems(prevItems => [...(multiple ? prevItems : []), ...newItems]);
        setError(false);
      } else {
        setError(true);
      }
    };
    const onDownload = (file: File, fileName?: string) => {
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };
    const onRemove = (index: number) => {
      setItems(prevFiles => {
        const newFiles = [...prevFiles];
        newFiles.splice(index, 1);
        return newFiles;
      });
    };

    const onRename = (index: number, newName: string) => {
      setItems(prevItems => {
        const items = [...prevItems];
        items[index].name = newName;
        return items;
      });
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      multiple,
      maxFiles: multiple ? maxFiles ? maxFiles : 0 : 1,
    });

    const uploadIcon = <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10M11.3333 5.33333L8 2M8 2L4.66667 5.33333M8 2V10" stroke="#18181B" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round" />
    </svg>

    if (itemDocument) {
      const actionsUnavailable = areActionsUnavailableBasedOnDocument(itemDocument);

      if (actionsUnavailable) {
        const message = "You cannot upload a file because this task is currently being processed. Actions are temporarily disabled. Please try again shortly.";

        return (
          <Box {...getRootProps()} className={classes.uploadBlocked} style={{ minHeight: uploadVersion === UPLOAD_VERSION_2 ? '77px' : '200px' }}>
            <div style={{ width: (uploadVersion === UPLOAD_VERSION_2 && items.length > 0) ? '100%' : 'auto' }}>
                <>
                  {uploadVersion === UPLOAD_VERSION_2 ?
                    <span className={classes.uploadTextV2}>{message}</span> :
                    <p className={classes.uploadText}>{message}</p>}
                </>
            </div>
          </Box>
        )
      }
    }

    return (
      <>
        {((multiple && !maxFiles) || (multiple && maxFiles && items.length < maxFiles) || (!multiple && items.length === 0)) &&
          <Box {...getRootProps()} className={error || showError ? classes.errorDragArea : classes.dragArea} style={{ minHeight: uploadVersion === UPLOAD_VERSION_2 ? '77px' : '200px' }}>
            <div style={{ width: (uploadVersion === UPLOAD_VERSION_2 && items.length > 0) ? '100%' : 'auto' }}>
              {uploadVersion !== UPLOAD_VERSION_2 ? <input {...getInputProps()} /> : <input disabled={items.length > 0} {...getInputProps()} />}
              {(uploadVersion === UPLOAD_VERSION_2) ? (items?.length === 0 ? uploadIcon : '') : <CloudUpload className={classes.icon} />}
              {isDragActive ? (
                <p>Drop the file(s) here...</p>
              ) : (
                <>
                  {uploadVersion === UPLOAD_VERSION_2 ? (items.length === 0 ? <span className={classes.uploadTextV2}>Upload</span> : '') : <p className={classes.uploadText}>Drag file to upload or <span style={{ textDecoration: 'underline' }}>browse file</span></p>}
                </>
              )}
              {uploadVersion === UPLOAD_VERSION_2 && items.length > 0 && items.map((item, i) => (
                <NewFileUploadItem
                  key={item.file?.name ? item.file.name : item.name}
                  name={item.name}
                  file={item.file}
                  version={UPLOAD_VERSION_2}
                  removeFileContainerBorder={true}
                  onDrop={onDrop}
                  disabled={item.disabled}
                  showUploadButton={showUploadButton}
                  showSimulateDeleteButton={showSimulateDeleteButton}
                  onSaveName={newName => onRename(i, newName)}
                  onDownload={() => onDownload(item.file, item.name)}
                  onRemove={() => onRemove(i)}
                />
              ))}
            </div>
          </Box>
        }
        {error && <Typography variant="caption" className={classes.errorMeasage}>
          * The file you are trying to upload does not have the expected file type
        </Typography>}
        <Box>
          {uploadVersion !== UPLOAD_VERSION_2 && items.map((item, i) => (
            <NewFileUploadItem
              key={item.file?.name ? item.file.name : item.name}
              name={item.name}
              file={item.file}
              onDrop={onDrop}
              disabled={item.disabled}
              showUploadButton={showUploadButton}
              onSaveName={newName => onRename(i, newName)}
              onRemove={() => onRemove(i)}
            />
          ))}
        </Box>
      </>
    );
  }
);